/**
 * Consistent Lifetime Avatar & Color System
 *
 * Provides deterministic user avatar palettes and initial extraction.
 * Seeding is based on permanent user identity (userId or normalized name),
 * ensuring that every user has a unique, beautiful custom color that
 * remains strictly consistent for lifetime across games, lobbies, and tables.
 */

export interface AvatarPalette {
  id: string;
  name: string;
  gradient: string;
  primary: string;
  glow: string;
  border: string;
}

export const AVATAR_PALETTES: AvatarPalette[] = [
  {
    id: 'crimson',
    name: 'Garnet Crimson',
    gradient: 'linear-gradient(135deg, #e11d48 0%, #881337 100%)',
    primary: '#e11d48',
    glow: 'rgba(225, 29, 72, 0.4)',
    border: 'rgba(251, 113, 133, 0.55)',
  },
  {
    id: 'amber',
    name: 'Imperial Amber',
    gradient: 'linear-gradient(135deg, #d97706 0%, #78350f 100%)',
    primary: '#d97706',
    glow: 'rgba(217, 119, 6, 0.4)',
    border: 'rgba(251, 191, 36, 0.55)',
  },
  {
    id: 'violet',
    name: 'Royal Violet',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
    primary: '#7c3aed',
    glow: 'rgba(124, 58, 237, 0.4)',
    border: 'rgba(167, 139, 250, 0.55)',
  },
  {
    id: 'sapphire',
    name: 'Cobalt Sapphire',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
    primary: '#2563eb',
    glow: 'rgba(37, 99, 235, 0.4)',
    border: 'rgba(96, 165, 250, 0.55)',
  },
  {
    id: 'emerald',
    name: 'Deep Emerald',
    gradient: 'linear-gradient(135deg, #059669 0%, #064e3b 100%)',
    primary: '#059669',
    glow: 'rgba(5, 150, 105, 0.4)',
    border: 'rgba(52, 211, 153, 0.55)',
  },
  {
    id: 'ruby',
    name: 'Scarlet Ruby',
    gradient: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
    primary: '#dc2626',
    glow: 'rgba(220, 38, 38, 0.4)',
    border: 'rgba(248, 113, 113, 0.55)',
  },
  {
    id: 'teal',
    name: 'Marine Teal',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
    primary: '#0d9488',
    glow: 'rgba(13, 148, 136, 0.4)',
    border: 'rgba(45, 212, 191, 0.55)',
  },
  {
    id: 'orchid',
    name: 'Wild Orchid',
    gradient: 'linear-gradient(135deg, #c026d3 0%, #701a75 100%)',
    primary: '#c026d3',
    glow: 'rgba(192, 38, 211, 0.4)',
    border: 'rgba(232, 121, 249, 0.55)',
  },
  {
    id: 'copper',
    name: 'Burnt Copper',
    gradient: 'linear-gradient(135deg, #ea580c 0%, #7c2d12 100%)',
    primary: '#ea580c',
    glow: 'rgba(234, 88, 12, 0.4)',
    border: 'rgba(251, 146, 60, 0.55)',
  },
  {
    id: 'indigo',
    name: 'Twilight Indigo',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #312e81 100%)',
    primary: '#4f46e5',
    glow: 'rgba(79, 70, 229, 0.4)',
    border: 'rgba(129, 140, 248, 0.55)',
  },
  {
    id: 'plum',
    name: 'Damson Plum',
    gradient: 'linear-gradient(135deg, #9333ea 0%, #581c87 100%)',
    primary: '#9333ea',
    glow: 'rgba(147, 51, 234, 0.4)',
    border: 'rgba(192, 132, 252, 0.55)',
  },
  {
    id: 'cyan',
    name: 'Glacier Cyan',
    gradient: 'linear-gradient(135deg, #0891b2 0%, #164e63 100%)',
    primary: '#0891b2',
    glow: 'rgba(8, 145, 178, 0.4)',
    border: 'rgba(34, 211, 238, 0.55)',
  },
  {
    id: 'rose',
    name: 'Vintage Rose',
    gradient: 'linear-gradient(135deg, #e11d48 0%, #9f1239 100%)',
    primary: '#e11d48',
    glow: 'rgba(225, 29, 72, 0.4)',
    border: 'rgba(251, 113, 133, 0.55)',
  },
  {
    id: 'bronze',
    name: 'Smoked Bronze',
    gradient: 'linear-gradient(135deg, #b45309 0%, #451a03 100%)',
    primary: '#b45309',
    glow: 'rgba(180, 83, 9, 0.4)',
    border: 'rgba(245, 158, 11, 0.55)',
  },
  {
    id: 'slate',
    name: 'Obsidian Steel',
    gradient: 'linear-gradient(135deg, #475569 0%, #0f172a 100%)',
    primary: '#475569',
    glow: 'rgba(71, 85, 105, 0.4)',
    border: 'rgba(148, 163, 184, 0.55)',
  },
  {
    id: 'jade',
    name: 'Imperial Jade',
    gradient: 'linear-gradient(135deg, #047857 0%, #064e3b 100%)',
    primary: '#047857',
    glow: 'rgba(4, 120, 87, 0.4)',
    border: 'rgba(16, 185, 129, 0.55)',
  },
];

/**
 * Deterministic 32-bit FNV-1a hash of a string.
 * Guarantees consistent lifetime output across all platforms and sessions.
 */
export function hashString(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * Safely extracts the first non-whitespace character as an uppercase initial,
 * supporting unicode and Cyrillic scripts.
 */
export function getUserInitial(name?: string | null): string {
  if (!name) return '?';
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const first = Array.from(trimmed)[0];
  return first ? first.toUpperCase() : '?';
}

export interface UserAvatarStyle {
  initial: string;
  palette: AvatarPalette;
  gradient: string;
  borderColor: string;
  glow: string;
}

/**
 * Retrieves the permanent avatar visual parameters for a user.
 * Prioritizes `userId` (when authenticated) for absolute lifetime persistence,
 * falling back to normalized display name.
 */
export function getUserAvatar(userId?: string | null, name?: string | null): UserAvatarStyle {
  const cleanName = (name || '').trim();
  const initial = getUserInitial(cleanName);

  const seed = (userId && userId.trim().length > 0)
    ? userId.trim()
    : cleanName.toLowerCase() || 'player';

  const hash = hashString(seed);
  const palette = AVATAR_PALETTES[hash % AVATAR_PALETTES.length];

  return {
    initial,
    palette,
    gradient: palette.gradient,
    borderColor: palette.border,
    glow: palette.glow,
  };
}
